// Copyright © 2019-2026 Focal Systems. All rights reserved.

interface Stop {
  at: number;
  rgb: [number, number, number];
}

/**
 * Stops of the uptime heat ramp, evenly spaced every 0.2.
 *
 * Above the pivot at 0.4 sit the WiFi coverage grades from
 * `store-map-coverage-overlay.tsx`, read in reverse, so a band shares its vocabulary with
 * the map overlay: the red a customer integrator already reads as "poor signal" is the
 * same red that means "poor uptime". Packing those four grades into the top 60% is what
 * buys resolution where real data lives — uptime clusters between 0.9 and 1.0, and
 * spreading green over the whole range left every healthy store the same shade.
 *
 * Below the pivot the ramp keeps descending into maroon rather than bottoming out at red.
 * A store under 40% is not "worse than poor", it is broken, and darkness reads as
 * severity at a glance in a way another red cannot.
 *
 * Fixed sRGB rather than theme tokens, for the same reason the map overlay fixes its
 * colours: a band has to carry its meaning identically in both themes.
 */
const DEAD: Stop = { at: 0, rgb: [69, 10, 10] }; // #450a0a — dark maroon
const SEVERE: Stop = { at: 0.2, rgb: [153, 27, 27] }; // #991b1b
const POOR: Stop = { at: 0.4, rgb: [220, 38, 38] }; // #dc2626
const FAIR: Stop = { at: 0.6, rgb: [217, 119, 6] }; // #d97706
const GOOD: Stop = { at: 0.8, rgb: [101, 163, 13] }; // #65a30d
const EXCELLENT: Stop = { at: 1, rgb: [5, 150, 105] }; // #059669

const UPTIME_STOPS: Stop[] = [DEAD, SEVERE, POOR, FAIR, GOOD, EXCELLENT];

interface Segment {
  lower: Stop;
  upper: Stop;
}

// Named rather than indexed off the stops so the lookup below has a total fallback and
// never has to reach past the end of an array.
const TOP_SEGMENT: Segment = { lower: GOOD, upper: EXCELLENT };
const UPTIME_SEGMENTS: Segment[] = [
  { lower: DEAD, upper: SEVERE },
  { lower: SEVERE, upper: POOR },
  { lower: POOR, upper: FAIR },
  { lower: FAIR, upper: GOOD },
  TOP_SEGMENT,
];

function mix(from: number, to: number, ratio: number): number {
  return Math.round(from + (to - from) * ratio);
}

/**
 * Fill colour for one uptime cell, as a CSS colour.
 *
 * `uptime` is a 0–1 ratio. Values outside that range are clamped rather than rejected: a
 * rounding artifact at 1.0000001 should paint green, not throw.
 */
export function uptimeSwatch(uptime: number): string {
  const value = Math.min(1, Math.max(0, uptime));
  const { lower, upper } = UPTIME_SEGMENTS.find((segment) => value <= segment.upper.at) ?? TOP_SEGMENT;
  const span = upper.at - lower.at;
  const ratio = span === 0 ? 0 : (value - lower.at) / span;
  const r = mix(lower.rgb[0], upper.rgb[0], ratio);
  const g = mix(lower.rgb[1], upper.rgb[1], ratio);
  const b = mix(lower.rgb[2], upper.rgb[2], ratio);
  return `rgb(${r} ${g} ${b})`;
}

/** The ramp as CSS gradient stops, for the legend. */
export const UPTIME_GRADIENT = UPTIME_STOPS.map(
  (stop) => `rgb(${stop.rgb[0]} ${stop.rgb[1]} ${stop.rgb[2]}) ${Math.round(stop.at * 100)}%`,
).join(", ");

/**
 * An uptime ratio as a percentage, to at most two decimals.
 *
 * Trailing zeros are dropped rather than padded: a perfect hour reads "100", not
 * "100.00", and the two decimals only show up when they carry something — the difference
 * between 99.75% and 99.8% is two cameras on a big store, which is exactly the detail
 * someone opens this tooltip to find.
 */
export function formatUptimePercent(uptime: number): string {
  return String(Number((uptime * 100).toFixed(2)));
}
